import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Image as ImageIcon, Video, Loader, Camera } from 'lucide-react';
import StoryEditor from './StoryEditor';
import StoryCameraCapture from './StoryCameraCapture';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { useCT } from '../utils/creationTranslations';

const AddStoryModal = ({ onClose, onSuccess }) => {
  const keyboardHeight = useKeyboardHeight();
  const ct = useCT();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);

  // Lock background scroll while modal is open (prevents body scrolling on iOS)
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier la taille (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error(ct('story.errTooBig'));
      return;
    }

    // Déterminer le type
    const type = file.type.startsWith('image/') ? 'image' : 
                 file.type.startsWith('video/') ? 'video' : null;
    
    if (!type) {
      toast.error(ct('story.errFormat'));
      return;
    }

    // Vérifier la durée pour les vidéos
    if (type === 'video') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        if (video.duration > 60) {
          toast.error(ct('story.errTooLong'));
          return;
        }
        
        // OK, on peut continuer
        setSelectedFile(file);
        setMediaType(type);
        setPreview(URL.createObjectURL(file));
      };
      
      video.src = URL.createObjectURL(file);
    } else {
      setSelectedFile(file);
      setMediaType(type);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = async (overlays = []) => {
    if (!selectedFile || !mediaType) {
      toast.error(ct('story.errPick'));
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('media_file', selectedFile);
      formData.append('media_type', mediaType);
      if (overlays && overlays.length > 0) {
        formData.append('overlays_json', JSON.stringify(overlays));
      }

      await axios.post('/api/stories', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(ct('story.published'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error uploading story:', error);
      toast.error(error.response?.data?.detail || ct('story.errPublish'));
    } finally {
      setUploading(false);
    }
  };

  return ReactDOM.createPortal((
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2147483647] flex items-start sm:items-center justify-center p-4 pt-[max(env(safe-area-inset-top),1.5rem)] overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchMove={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}
      data-testid="story-modal-backdrop"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="bg-gray-900 rounded-3xl max-w-lg w-full flex flex-col border-2 border-violet-500/40 shadow-[0_0_40px_-10px_rgba(168,85,247,0.45)] overflow-hidden" style={{ maxHeight: `calc(88vh - ${keyboardHeight}px)` }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="border-b border-white/10 p-5 flex items-center justify-between flex-shrink-0 rounded-t-3xl">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-magenta-400 bg-clip-text text-transparent">
            {ct('story.modalTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="close-story-modal-btn"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Content (scrollable middle) */}
        <div className="flex-1 overflow-y-auto p-5" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
          {!preview ? (
            <div>
              <p className="text-gray-400 mb-4">
                {ct('story.subtitle')}
              </p>

              {/* Camera capture (Phase 1) — opens an in-app fullscreen camera with
                  photo/video modes, front/back switch and a 30-second video cap.
                  After capture, the user lands in StoryEditor for text overlays. */}
              <button
                onClick={() => setShowCamera(true)}
                className="w-full mb-4 px-5 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-magenta-600 hover:from-violet-500 hover:to-magenta-500 transition-all shadow-lg shadow-violet-900/30 flex items-center justify-center gap-3 font-semibold"
                data-testid="open-story-camera-btn"
              >
                <Camera className="w-6 h-6" />
                <span>{ct('story.openCamera')}</span>
              </button>

              {/* Upload Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full aspect-square bg-white/5 border-2 border-dashed border-white/20 rounded-2xl flex flex-col items-center justify-center hover:border-magenta-500/50 hover:bg-white/10 transition-all group"
                data-testid="select-story-file-btn"
              >
                <Upload className="w-16 h-16 text-gray-400 group-hover:text-magenta-400 transition-colors mb-4" />
                <p className="text-gray-400 group-hover:text-white transition-colors font-medium">
                  {ct('story.tapToPick')}
                </p>
                <p className="text-sm text-gray-500 mt-2">{ct('story.hint')}</p>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Format Info */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-white/5 rounded-xl p-4 flex items-center space-x-3">
                  <ImageIcon className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="font-semibold text-sm">{ct('story.images')}</p>
                    <p className="text-xs text-gray-400">{ct('story.imgFormats')}</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4 flex items-center space-x-3">
                  <Video className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="font-semibold text-sm">{ct('story.videos')}</p>
                    <p className="text-xs text-gray-400">{ct('story.vidFormats')}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Preview */}
              <div className="relative aspect-[9/16] max-h-[55vh] bg-black rounded-2xl overflow-hidden">
                {mediaType === 'image' ? (
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <video
                    src={preview}
                    controls
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with action buttons (always visible / sticky bottom) */}
        {preview && (
          <div className="border-t border-white/10 p-4 flex-shrink-0 flex gap-3 rounded-b-3xl">
            <button
              onClick={() => {
                setSelectedFile(null);
                setPreview(null);
                setMediaType(null);
              }}
              className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-xl font-semibold transition-colors"
              disabled={uploading}
              data-testid="change-story-file-btn"
            >
              {ct('story.change')}
            </button>
            <button
              onClick={() => handleUpload([])}
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="publish-story-skip-btn"
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  {ct('story.publishing')}
                </>
              ) : (
                ct('story.publishRaw')
              )}
            </button>
            <button
              onClick={() => setShowEditor(true)}
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-500 to-magenta-600 hover:from-violet-600 hover:to-magenta-700 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="story-edit-text-btn"
            >
              {ct('story.addText')}
            </button>
          </div>
        )}
      </div>
      {showEditor && preview && (
        <StoryEditor
          preview={preview}
          mediaType={mediaType}
          publishing={uploading}
          onCancel={() => setShowEditor(false)}
          onPublish={(overlays) => handleUpload(overlays)}
        />
      )}
      {showCamera && (
        <StoryCameraCapture
          onCancel={() => setShowCamera(false)}
          onCapture={(file, type) => {
            // Wire the camera capture straight into the existing preview flow,
            // then open StoryEditor so the user can drop text on top.
            setSelectedFile(file);
            setMediaType(type);
            setPreview(URL.createObjectURL(file));
            setShowCamera(false);
            // Auto-open the text/sticker editor right after capture, matching
            // the UX of Instagram / Snapchat where you land in the editor.
            setTimeout(() => setShowEditor(true), 50);
          }}
        />
      )}
    </div>
  ), document.body);
};

export default AddStoryModal;
